import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

const ResetPassword = () => {
  const { branding } = useBranding();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(`/api/auth/verify-reset-token?token=${token}`);
        const data = await response.json();
        
        setTokenValid(data.valid);
        if (data.valid) {
          setEmail(data.email);
        } else {
          setError(data.error || 'Invalid token');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Failed to verify token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Password reset failed');
      }
    } catch (err) {
      setError('Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="loading-spinner" />
            <h1>Verifying Link...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <XCircle size={64} style={{ color: '#ef4444' }} />
            <h1>Invalid Link</h1>
            <p style={{ color: '#ef4444' }}>{error || 'This password reset link is invalid or has expired.'}</p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/forgot-password" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Request New Link
            </Link>
          </div>

          {(!branding.isWhiteLabeled || branding.poweredByVisible) && (
            <div className={branding.isWhiteLabeled ? 'powered-by-footer' : 'login-footer'}>
              {branding.isWhiteLabeled ? 'Powered by Serverli' : <p>Powered by Serverli</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <CheckCircle size={64} style={{ color: '#10b981' }} />
            <h1>Password Reset!</h1>
            <p>Your password has been successfully reset.</p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/login" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Continue to Login
            </Link>
          </div>

          {(!branding.isWhiteLabeled || branding.poweredByVisible) && (
            <div className={branding.isWhiteLabeled ? 'powered-by-footer' : 'login-footer'}>
              {branding.isWhiteLabeled ? 'Powered by Serverli' : <p>Powered by Serverli</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo-wrapper">
            <img
              src={branding.brandLogoUrl || '/serverliccm-logo.png'}
              alt={`${branding.brandName} Logo`}
              className="login-logo-img"
            />
          </div>
          <h1>Reset Password</h1>
          <p>Create a new password for <strong>{email}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              New Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading}
          >
            {loading ? (
              <>Resetting...</>
            ) : (
              <>
                <Lock size={18} />
                Reset Password
              </>
            )}
          </button>
        </form>

        {(!branding.isWhiteLabeled || branding.poweredByVisible) && (
          <div className={branding.isWhiteLabeled ? 'powered-by-footer' : 'login-footer'}>
            {branding.isWhiteLabeled ? 'Powered by Serverli' : <p>Powered by Serverli</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
